import { useState, useCallback } from 'react';
import { Download, FileSpreadsheet, FileArchive, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useStore } from '../store/useStore';
import { generateEnrichedExcel } from '../lib/excelExporter';
import { generateHtmlZip } from '../lib/htmlExporter';
import { saveAs } from 'file-saver';

export function ExportPanel() {
  const { studentDataList } = useStore();
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exportExcel = useCallback(async () => {
    if (studentDataList.length === 0) {
      setError('没有可导出的数据');
      return;
    }

    setIsExportingExcel(true);
    setError(null);
    setSuccess(null);

    try {
      const excelBuffer = generateEnrichedExcel(studentDataList);
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `体育评价_${new Date().toLocaleDateString()}.xlsx`);
      setSuccess('Excel 文件导出成功');
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    } finally {
      setIsExportingExcel(false);
    }
  }, [studentDataList]);

  const exportZip = useCallback(async () => {
    if (studentDataList.length === 0) {
      setError('没有可导出的数据');
      return;
    }

    setIsExportingZip(true);
    setError(null);
    setSuccess(null);

    try {
      const zipBlob = await generateHtmlZip(studentDataList);
      saveAs(zipBlob, `评价报告包_${new Date().toLocaleDateString()}.zip`);
      setSuccess('HTML 报告包导出成功');
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    } finally {
      setIsExportingZip(false);
    }
  }, [studentDataList]);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <Download className="w-8 h-8 text-blue-600" />
          导出中心
        </h2>

        {studentDataList.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>暂无数据可导出，请先上传学生评价数据</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                共 <span className="font-semibold">{studentDataList.length}</span> 名学生的评价数据
              </p>
            </div>

            <button
              onClick={exportExcel}
              disabled={isExportingExcel}
              className="w-full flex items-center justify-center gap-3 p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExportingExcel ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-6 h-6" />
              )}
              <span className="font-medium">
                {isExportingExcel ? '导出中...' : '导出富化 Excel'}
              </span>
            </button>
            <p className="text-xs text-gray-500 text-center">
              包含所有评分数据及 AI 生成的微观评语
            </p>

            <div className="border-t border-gray-200 pt-6">
              <button
                onClick={exportZip}
                disabled={isExportingZip}
                className="w-full flex items-center justify-center gap-3 p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExportingZip ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <FileArchive className="w-6 h-6" />
                )}
                <span className="font-medium">
                  {isExportingZip ? '打包中...' : '导出 HTML 报告包'}
                </span>
              </button>
              <p className="text-xs text-gray-500 text-center">
                生成包含所有学生独立 HTML 报告的 ZIP 压缩包
              </p>
            </div>

            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
                <CheckCircle className="w-5 h-5" />
                {success}
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}